import { useCallback, useMemo } from 'react';
import Handlebars from 'handlebars';
//@ts-ignore
import _ from 'lodash';
import dayjs from 'dayjs';
import { useAppSelector } from '../redux/Store';
import { templates } from '../utils/precompiledTemplates'; // ✅ Import precompiled templates
import customParseFormat from 'dayjs/plugin/customParseFormat';

dayjs.extend(customParseFormat);

export const useTemplateHelper = () => {
  const defaultTemplateKey = 'defaultTemplate';
  const enumTemplateKey = 'enumTemplate';
  const luggage = useAppSelector((state) => state.inspactOrder.luggage);
  const languagePack = useAppSelector(
    (state) => state.language.inspectionSidebarLanguagePack.keyValues
  );

  const memoizedLuggage = useMemo(() => luggage, [luggage]);

  const resolveReference = useCallback(
    (propReference: string) => {
      if (!memoizedLuggage || !propReference) return null;

      let refObj: any = memoizedLuggage;

      for (const part of propReference.split(':')) {
        refObj = refObj[part];
        if (!refObj) {
          break;
        }
      }
      return refObj;
    },
    [memoizedLuggage]
  );

  // ✅ Register Handlebars Helpers (Ensure they exist before using templates)
  useMemo(() => {
    Handlebars.registerHelper('translate', function (value: string) {
      return languagePack?.[value] || value;
    });

    Handlebars.registerHelper('enum', function (value: string) {
      return value?.split('.').pop();
    });

    Handlebars.registerHelper('first', function (value: any) {
      return Array.isArray(value) && value.length > 0 ? value[0]?.value : '';
    });

    Handlebars.registerHelper(
      'match',
      function (pattern: string, text: string) {
        return text.match(pattern);
      }
    );

    Handlebars.registerHelper(
      'link',
      function (
        toTemplate: string,
        textTemplate: string,
        { data: { root: model } }
      ) {
        const to = templates[toTemplate](model);
        const text = templates[textTemplate](model);
        return new Handlebars.SafeString(
          `<a href='${to}' style="color: white; text-decoration: underline;">${text}</a>`
        );
      }
    );

    Handlebars.registerHelper(
      'date',
      function (dateString: any, dateFormat: string) {
        if (Array.isArray(dateString)) {
          dateString = dateString[0]?.value;
        } else if (typeof dateString === 'object') {
          dateString = dateString?.value;
        }
        if (!dateString) return '';
        const parsedDate = dayjs(dateString, 'YYYY-MM-DD HH:mm:ss');

        return parsedDate.isValid()
          ? parsedDate.format(dateFormat || 'MM/DD/YYYY')
          : '';
      }
    );

    Handlebars.registerHelper('raw', function (value: string) {
      return new Handlebars.SafeString(value);
    });

    Handlebars.registerHelper(
      'filterTruthyValues',
      function (...values: any[]) {
        values.pop(); // Remove Handlebars options object
        return values.filter(Boolean);
      }
    );

    Handlebars.registerHelper(
      'formatAddress',
      function (street: string, city: string, state: string, zip: string) {
        const parts = [street, city && `${city},`, state, zip].filter(Boolean);
        return new Handlebars.SafeString(
          `${street}<br/>${city}, ${state}  ${zip}`
        );
      }
    );
  }, [languagePack]);

  // ✅ Uses Precompiled Templates Instead of `Handlebars.compile`
  const applyTemplate = useCallback(
    (templateKey: keyof typeof templates, data: any) => {
      return templates[templateKey](data);
    },
    []
  );

  const generateModel = useCallback(
    (props: any) => {
      return _.zipObject(
        Object.keys(props),
        Object.keys(props).map((k) =>
          applyTemplate(defaultTemplateKey, resolveReference(props[k]))
        )
      );
    },
    [applyTemplate, resolveReference]
  );

  const SideBarTemplate = useMemo(() => {
    return {
      headings: [
        // { caption: 'Work Order' },
        // {
        //   caption: 'Order #',
        //   valueRef: 'propertyRecord:questions:WorkOrder.OrderNumber',
        // },
        // {
        //   caption: 'Loan #',
        //   valueRef: 'propertyRecord:questions:Property.LoanNumber',
        // },
        // {
        //   caption: 'Property ID',
        //   valueRef: 'propertyRecord:questions:Property.ID',
        // },
        // {
        //   caption: 'Prop Loan ID',
        //   valueRef: 'propertyRecord:questions:Property.PropertyLoanId',
        // },
      ],
      groups: [
        {
          caption: 'Work Order',
          fields: [
            {
              caption: 'Order #',
              valueRef: 'propertyRecord:questions:WorkOrder.OrderNumber',
            },
            {
              caption: 'Status',
              valueRef:
                'propertyRecord:questions:WorkOrder.TblStateExternalName',
            },
            {
              caption: 'Workcode',
              valueRef: 'propertyRecord:questions:WorkOrder.TblWorkCode',
            },
            {
              caption: 'Opened',
              valueRef: 'propertyRecord:questions:WorkOrder.TblOpenDt',
              templateKey: `dateTemplate`,
            },
            {
              caption: 'Due',
              valueRef: 'propertyRecord:questions:WorkOrder.TblDueDt',
              templateKey: `dateTemplate`,
            },
            {
              caption: '_Special Instructions',
              valueRef: 'propertyRecord:questions:WorkOrder.TblInstructions',
            },
          ].filter((field) => resolveReference(field.valueRef)),
        },
        {
          caption: 'Vendor Information',
          fields: [
            {
              caption: 'Vendor Code',
              valueRef: 'propertyRecord:questions:WorkOrder.TblVendorCode',
            },
            {
              caption: 'Submitted by',
              valueRef:
                'propertyRecord:questions:WorkOrder.ActualSubmittingVendorCode',
            },
            {
              caption: 'Submit Date',
              valueRef: 'propertyRecord:questions:WorkOrder.TblVendorSubmitDt',
              templateKey: 'dateTemplate',
            },
            {
              caption: 'Complete Date',
              valueRef:
                'propertyRecord:questions:WorkOrder.TblVendorCompleteDt',
              templateKey: 'dateTemplate',
            },
          ].filter((field) => resolveReference(field.valueRef)),
        },
        {
          caption: 'Property Information',
          fields: [
            {
              caption: '_Address',
              street: 'propertyRecord:questions:Property.TblAddress1',
              city: 'propertyRecord:questions:Property.City',
              state: 'propertyRecord:questions:Property.State',
              zip: 'propertyRecord:questions:Property.ZipCode',
              templateKey: 'formatAddressTemplate',
            },
            {
              caption: 'Occupancy Status',
              valueRef: 'propertyRecord:questions:WorkOrder.TblOccupancyStatus',
              templateKey: 'enumTemplate',
            },
            {
              caption: 'Client Code',
              valueRef: 'propertyRecord:questions:Property.ClientCode',
            },
            {
              caption: 'Loan Type',
              valueRef: 'propertyRecord:questions:Property.LoanType',
              templateKey: 'enumTemplate',
            },
          ],
        },
      ],
    };
  }, [resolveReference]);

  return {
    SideBarTemplate,
    memoizedLuggage,
    languagePack,
    defaultTemplateKey,
    enumTemplateKey,
    resolveReference,
    applyTemplate,
    generateModel,
  };
};
